import { api } from '@/lib/axios';

export interface ActivityLogEntry { description: string; timestamp: string; user: string; }

export interface PACSImage {
  study_uid: string;
  series_uid: string;
  uid: string;
  number: number;
  imageID: string;
  /** PACS File element — EPW stream key prefix (Gen2 epwFileName). */
  file: string;
  /** 1-based frame index for GetImage proxy. */
  frame: string;
  /** PACS Frames count — EPW stream key suffix (Gen2 epwFrame). */
  frames: string;
  keyImage: string;
  practiceCode: string;
  pacsServerCode: string;
  siteCode: string;
}

export interface PACSSeries {
  accession_number: string;
  study_uid: string;
  number: number;
  modality: string;
  description: string;
  display_order: number;
  pacsImage: PACSImage[];
}

export interface PACSStudy {
  pacs_Server_type_code: string;
  practiceCode: string;
  study_uid: string;
  study_status: number;
  /** True only for eRAD V9 — cook/thaw status gates the Images preparing banner. */
  cookStatusRelevant?: boolean;
  pacsSeries: PACSSeries[];
  dicomDownloadSupported: boolean;
  isHeldBack: boolean;
  availableDate: string | null;
  holdBackMessage: string | null;
  safetyAlert: string | null;
}

/** Response from GET /api/PACS/StreamingConfig */
export type PacsStreamingMode = 'sdk' | 'proxy' | 'error';

export interface PacsStreamingConfig {
  mode: PacsStreamingMode;
  epwStreamingServerAddress?: string | null;
  pacsSessionId?: string | null;
  studyUid?: string | null;
  studyKey?: string | null;
  /** Present when mode is proxy/error — why SDK path was not used. */
  reason?: string | null;
  /**
   * Gen2 EPWWebAppErrorImageloading. When `"error"` (default), WebSocket failures
   * show PPPACSSDKStreamErrorLoading; otherwise fall back to GetImage.
   */
  onStreamFailure?: string | null;
}

export interface DicomRequestResponse {
  mediaSessionId: string;
  mediaRequestKey?: number;
  mediaType?: DicomMediaType | string;
  progress?: string | null;
}
export type DicomMediaState = 'queued' | 'inprogress' | 'ready' | 'error';
export type DicomMediaType = 'USB' | 'DVD';
export interface DicomStatusResponse {
  state: DicomMediaState;
  queuePosition?: number;
  progressPercent?: number;
}

/** Owned / visible DICOM media request row (GET /PACS/DicomMedia/Active|History). */
export interface DicomMediaListItem {
  mediaRequestKey: number;
  mediaSessionId: string | null;
  studyKey: number;
  patientKey: number;
  accessionNumber: string | null;
  examDescription: string | null;
  mediaType: string | null;
  progress: string | null;
  errorNote: string | null;
  active: boolean;
  isComplete: boolean;
  isAvailable: boolean;
  createdDate: string;
  completedDate: string | null;
  downloadDate: string | null;
  requestedBy: string | null;
  notifyDestination: string | null;
  pacsServerCode: string | null;
  /** Bytes on disk when the ready media file is available; omitted/null otherwise. */
  fileSizeBytes?: number | null;
  /** PACS Study Instance UID — preferred match key when study_key on the row is wrong/stale. */
  studyInstanceUid?: string | null;
}

export interface DicomRequestOptions {
  studyUid?: string;
  studyKey?: number;
  pacsServerCode?: string;
  practiceCode?: string;
  mediaType?: DicomMediaType;
  notify?: boolean;
  notifyDestination?: string;
  shareToken?: string;
}

export interface DicomNotifyUpdateRequest {
  notify: boolean;
  notifyDestination?: string | null;
}

export interface ExamRecordExam {
  studyKey: number;
  orderKey: number;
  studyDate: string;
  modalityCode?: string;
  procedureDescription?: string;
  accessionNumber?: string;
  appointmentId?: string;
  siteName?: string;
  siteCode?: string;
}

export interface ExamRecord {
  studyKey: number;
  orderKey: number;
  studyDate: string;
  modalityCode: string;
  procedureDescription: string;
  accessionNumber?: string;
  appointmentId?: string;
  siteName?: string;
  siteCode?: string;
  status?: string;
  reportStatus?: string;
  studyStatusDescription?: string;
  orderingPhysicianName?: string;
  clinicalSummary?: string;
  reportText?: string;
  patientId?: string;
  pacsAvailable?: boolean;
  /** False when the order was placed directly in the RIS (not via the patient or referring portal).
   *  RIS-placed orders must be cancelled by calling the facility. Defaults to true when absent. */
  canCancelOnline?: boolean;
  /** True when a Good Faith Estimate scan document exists for this order. Drives GFE-tab visibility
   *  from the list so we don't probe /Order/{id}/GoodFaithEstimateDocument on every navigation. */
  hasGoodFaithEstimate?: boolean;
  /** True when the diagnostic report can be opened now (approved, signed, hold-back elapsed).
   *  Computed by PortalApi with the same gate as /Study/DiagnosticReport. Exam list rows only.
   *  Null when upstream did not compute it — callers should fall back to status. */
  reportAvailable?: boolean | null;
  /** True when the hub Report card should show a New indicator (available, unread, within window). */
  isNewReport?: boolean;
  /** Full study-key set represented by this row when the backend grouped a multi-study appointment. */
  studyKeys?: number[];
  /** Exam rows represented by this appointment/search row. */
  exams?: ExamRecordExam[];
}

export interface ExamSearchRequest {
  Skip: number;
  Take: number;
  SearchText?: string;
  ListType?: string;
  /** When true, PortalApi writes a one-shot "Exam list viewed" audit for this patient. */
  RecordAudit?: boolean;
}
export interface ExamSearchResponse { rowCount: number; result: ExamRecord[]; }

export type ReportUnavailabilityReason =
  | 'Available'
  | 'HeldBack'
  | 'NoInterpretation'
  | 'NotSigned'
  | 'LayLetterPending'
  | 'ExamDoneTechOnly'
  | 'ExtReportVerified'
  | 'IncompleteStatus'
  | 'LegacyPdfOnly';

export interface DiagnosticReportResponse {
  report: string | null;
  summary: string | null;
  isHeldBack: boolean;
  availableDate: string | null;
  holdBackMessage: string | null;
  safetyAlert: string | null;
  isPdfEnabled: boolean;
  unavailabilityReason: ReportUnavailabilityReason;
  statFlag: boolean;
  hasLinkedStudy: boolean;
}

export interface LayLetterStatusResponse {
  required: boolean;
  acknowledged: boolean;
  isHeldBack: boolean;
  availableDate: string | null;
}

export interface LayLetterPageResponse {
  pageNumber: number;
  dataUrl: string;
}

export interface LayLetterDocumentResponse {
  pages: LayLetterPageResponse[];
  isHeldBack: boolean;
  availableDate: string | null;
  holdBackMessage: string | null;
  totalPageCount: number;
}

/** The scanned Good Faith Estimate document generated alongside an order — unrelated to the GFE cost-estimate calculator (gfeService). */
export interface GoodFaithEstimatePageResponse {
  pageNumber: number;
  dataUrl: string;
}

export interface GoodFaithEstimateDocumentResponse {
  pages: GoodFaithEstimatePageResponse[];
}

export const examService = {
  search: (req: ExamSearchRequest) =>
    api.post<ExamSearchResponse>('/Worklist/ExamSearch/_search', req).then(r => r.data),

  getDiagnosticReport: (studyKey: number) =>
    api.get<DiagnosticReportResponse>(`/Study/DiagnosticReport/${studyKey}`).then(r => r.data),

  getLayLetterDocument: (studyKey: number) =>
    api.get<LayLetterDocumentResponse>(`/Study/${studyKey}/LayLetterDocument`).then(r => r.data),

  getLayLetterStatus: (studyKey: number) =>
    api.get<LayLetterStatusResponse>(`/Study/${studyKey}/LayLetterStatus`).then(r => r.data),

  /** True when a LayLetter scan exists and the report hold period has expired (no page payloads). */
  hasAvailableLayLetter: (studyKey: number) =>
    api.get<boolean>(`/Study/${studyKey}/LayLetterAvailable`).then(r => r.data),

  acknowledgeLayLetter: (studyKey: number) =>
    api.post(`/Study/${studyKey}/LayLetterAcknowledge`).then(r => r.data),

  getOrderSummary: (orderKey: number) =>
    api.get<{ summary: string; details: Record<string, string> }>(`/Order/${orderKey}/summary`).then(r => r.data),

  getGoodFaithEstimateDocument: (orderKey: number, studyKey?: number) =>
    api.get<GoodFaithEstimateDocumentResponse>(`/Order/${orderKey}/GoodFaithEstimateDocument`, {
      params: studyKey != null && studyKey > 0 ? { studyKey } : undefined,
    }).then(r => r.data),

  auditGoodFaithEstimateDownload: (orderKey: number, studyKey?: number) =>
    api.post(`/Order/${orderKey}/GoodFaithEstimateDocument/DownloadAudit`, null, {
      params: studyKey != null && studyKey > 0 ? { studyKey } : undefined,
    }).then(() => {}),

  auditGoodFaithEstimatePrint: (orderKey: number, studyKey?: number) =>
    api.post(`/Order/${orderKey}/GoodFaithEstimateDocument/PrintAudit`, null, {
      params: studyKey != null && studyKey > 0 ? { studyKey } : undefined,
    }).then(() => {}),

  auditLayLetterDownload: (studyKey: number) =>
    api.post(`/Study/${studyKey}/LayLetterDocument/DownloadAudit`).then(() => {}),

  auditLayLetterPrint: (studyKey: number) =>
    api.post(`/Study/${studyKey}/LayLetterDocument/PrintAudit`).then(() => {}),

  /** Returns a PDF Blob — caller triggers download via downloadService.triggerDownload() */
  getDiagnosticReportPDF: (studyKey: number) =>
    api.get<Blob>(`/Study/DiagnosticReport/${studyKey}/PDF`, { responseType: 'blob' }).then(r => r.data),

  /** Returns a PDF Blob rendered by SSRS */
  getSsrsReportPdf: (studyKey: number) =>
    api.get<Blob>(`/Study/DiagnosticReport/${studyKey}/SSRS/PDF`, { responseType: 'blob' }).then(r => r.data),

  /** Returns an HTML string rendered by SSRS */
  getSsrsReportHtml: (studyKey: number) =>
    api.get<string>(`/Study/DiagnosticReport/${studyKey}/SSRS/HTML`, { responseType: 'text' }).then(r => r.data),

  getPACSStudy: (accessionNumber: string, patientId: string, shareAccessToken?: string, studyKey?: number) =>
    api.get<PACSStudy>('/PACS/GetPACSImageInfo', {
      params: {
        accessionNumber,
        patientId,
        ...(studyKey != null && studyKey > 0 ? { studyKey } : {}),
      },
      ...(shareAccessToken ? { headers: { Authorization: `Bearer ${shareAccessToken}` } } : {}),
    }).then(r => r.data),

  /** EPW streaming handshake — mode sdk | proxy | error (Gen2 LoadPACSByEPWWebApp gate). */
  getPacsStreamingConfig: (accessionNumber: string, patientId: string, shareAccessToken?: string) =>
    api.get<PacsStreamingConfig>('/PACS/StreamingConfig', {
      params: { accessionNumber, patientId },
      ...(shareAccessToken ? { headers: { Authorization: `Bearer ${shareAccessToken}` } } : {}),
    }).then(r => r.data),

  /**
   * Creates (or reuses study-wide active) DICOM media request.
   * Prefer mediaType USB (ZIP) / DVD (ISO). Notify is owner-only.
   */
  requestDicomDownload: (accessionNumber: string, patientId: string, options?: DicomRequestOptions) =>
    api.post<DicomRequestResponse>(
      '/PACS/DicomRequest',
      {
        accessionNumber,
        patientId,
        ...(options?.studyUid ? { studyUid: options.studyUid } : {}),
        ...(options?.studyKey != null && options.studyKey > 0 ? { studyKey: options.studyKey } : {}),
        ...(options?.pacsServerCode ? { pacsServerCode: options.pacsServerCode } : {}),
        ...(options?.practiceCode ? { practiceCode: options.practiceCode } : {}),
        mediaType: options?.mediaType ?? 'USB',
        ...(options?.notify != null ? { notify: options.notify } : {}),
        ...(options?.notifyDestination ? { notifyDestination: options.notifyDestination } : {}),
      },
      options?.shareToken ? { headers: { Authorization: `Bearer ${options.shareToken}` } } : undefined,
    ).then(r => r.data),

  /**
   * Status for an open dialog. The request may call PACS Check and queue a file pull;
   * it returns without waiting for the ZIP/ISO bytes.
   */
  checkDicomDownload: (sessionId: string, accessionNumber: string, shareToken?: string) =>
    api.get<DicomStatusResponse>(
      `/PACS/DicomRequest/${encodeURIComponent(sessionId)}`,
      {
        params: { accessionNumber },
        ...(shareToken ? { headers: { Authorization: `Bearer ${shareToken}` } } : {}),
      },
    ).then(r => r.data),

  downloadDicomBlob: (sessionId: string, shareToken?: string) =>
    api.get<Blob>(
      `/PACS/DicomDownload/${encodeURIComponent(sessionId)}`,
      {
        responseType: 'blob',
        ...(shareToken ? { headers: { Authorization: `Bearer ${shareToken}` } } : {}),
      },
    ).then(r => r.data),

  cancelDicomDownload: (sessionId: string) =>
    api.post(`/PACS/DicomRequest/${encodeURIComponent(sessionId)}/Cancel`).then(() => {}),

  listActiveDicomMedia: () =>
    api.get<DicomMediaListItem[]>('/PACS/DicomMedia/Active').then(r => r.data),

  listDicomMediaHistory: () =>
    api.get<DicomMediaListItem[]>('/PACS/DicomMedia/History').then(r => r.data),

  updateDicomNotify: (mediaRequestKey: number, body: DicomNotifyUpdateRequest) =>
    api.post(`/PACS/DicomMedia/${mediaRequestKey}/Notify`, body).then(() => {}),

  clearDicomMedia: (mediaRequestKey: number) =>
    api.post(`/PACS/DicomMedia/${mediaRequestKey}/Clear`).then(() => {}),

  /** Returns hold-back status for the CCDA tab before requesting document HTML. */
  getCcdaAvailability: (studyKey: number) =>
    api.get<CcdaAvailabilityResponse>(`/Study/${studyKey}/CCDA/Status`).then(r => r.data),

  /** Returns the CCDA already rendered as human-readable HTML (server-side XSLT transform) for inline display */
  getCcdaHtml: (studyKey: number) =>
    api.get<string>(`/Study/${studyKey}/CCDA`, { responseType: 'text' }).then(r => r.data),

  /** Downloads CCDA as HTML, XML, or ZIP depending on options — returns a Blob */
  downloadCcda: (studyKey: number, payload: CcdaDownloadRequest) =>
    api.post<Blob>(`/Study/${studyKey}/CCDA/Download`, payload, { responseType: 'blob' }).then(r => r.data),

  /** Sends CCDA by email or Direct message */
  sendCcda: (studyKey: number, payload: CcdaSendRequest) =>
    api.post(`/Study/${studyKey}/CCDA/Send`, payload).then(r => r.data),

  /** Returns exam detail panel info: dictated/signed physician names and signed date */
  getExamDetailInfo: (studyKey: number) =>
    api.get<ExamDetailInfo>(`/Study/${studyKey}/DetailInfo`).then(r => r.data),

  /** Returns SSRS-rendered clinical summary HTML for the Visit Summary tab */
  getClinicalSummaryHtml: (studyKey: number) =>
    api.get<ClinicalSummaryResponse>(`/Study/${studyKey}/ClinicalSummary`).then(r => r.data),

  /** Downloads a server-side PDF from SSRS for the Visit Summary — caller triggers download via triggerDownload() */
  downloadClinicalSummaryPdf: (studyKey: number) =>
    api.get<Blob>(`/Study/${studyKey}/ClinicalSummary/PDF`, { responseType: 'blob' }).then(r => r.data),

  /** Increments the print_summary_pp counter and writes a visit-summary print audit */
  recordPrintSummary: (studyKey: number) =>
    api.post(`/Study/${studyKey}/ClinicalSummary/PrintStat`).then(() => {}),

  /** Increments print_report_pp and writes a diagnostic-report print audit */
  recordPrintReport: (studyKey: number) =>
    api.post(`/Study/DiagnosticReport/${studyKey}/PrintStat`).then(() => {}),

  getActivityLog: (studyKey: number) =>
    api.get<ActivityLogEntry[]>(`/Study/${studyKey}/ActivityLog`).then(r => r.data),
};

export interface ClinicalSummaryResponse {
  available: boolean;
  html: string | null;
}

export interface ExamDetailInfo {
  isDictated: boolean;
  dictatedByName: string | null;
  isSigned: boolean;
  signedByName: string | null;
  signedDate: string | null;
}

export interface CcdaAvailabilityResponse {
  isHeldBack: boolean;
  availableDate: string | null;
  holdBackMessage: string | null;
}

export interface CcdaDownloadRequest {
  includeHtml: boolean;
  includeCcda: boolean;
  fileName: string;
}

export interface CcdaSendRequest {
  email: string;
  sendHtml: boolean;
  sendCcda: boolean;
  isSecure: boolean;
}
